import { useEffect, useState } from "react";
import axios from "axios";
import { getTenantId, installTenantInterceptor } from "@/lib/tenant";
import TenantSetupWizard from "@/components/TenantSetupWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Wraps PayPunch routes and gates them by tenant status:
 *   - "oakton" (default) → always allowed, no wizard.
 *   - Any other tenant that DOES NOT yet exist → show setup wizard.
 *   - Existing tenants → render children normally.
 *
 * Installs the tenant axios interceptor once on mount so every downstream
 * component's API calls automatically carry X-Tenant-Id.
 */
export default function TenantGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | setup | ready
  const tenantId = getTenantId();

  useEffect(() => {
    installTenantInterceptor();
    // Skip the wizard entirely for the default tenant (production Market Place)
    if (tenantId === "oakton") {
      setStatus("ready");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/tenant/current`, {
          headers: { "X-Tenant-Id": tenantId },
        });
        setStatus(res.data?.exists ? "ready" : "setup");
      } catch {
        // If the API is unreachable we still allow the app to render (offline-tolerant)
        setStatus("ready");
      }
    })();
  }, [tenantId]);

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center text-white/70 text-sm">
        Loading PayPunch…
      </div>
    );
  }
  if (status === "setup") {
    return <TenantSetupWizard tenantId={tenantId} onComplete={() => setStatus("ready")} />;
  }
  return children;
}
